(() => {
  const shuffle = items => {
    for (let index = items.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [items[index], items[other]] = [items[other], items[index]];
    }
    return items;
  };
  const randomCards = async (root, grid, initializeCards, applyFilter) => {
    const count = Number(grid.dataset.enProductCount);
    const pageCount = Math.ceil(Math.min(count, 25000) / 24);
    if (!Number.isSafeInteger(pageCount) || pageCount < 1 || !grid.dataset.enCollectionUrl) return;
    const controller = new AbortController();
    let interacted = grid.contains(document.activeElement);
    const stop = () => { interacted = true; controller.abort(); };
    const events = ['pointerdown', 'focusin', 'keydown', 'click'];
    events.forEach(name => grid.addEventListener(name, stop, {once: true}));
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      if (interacted) return;
      const base = new URL(grid.dataset.enCollectionUrl, window.location.origin);
      if (base.origin !== window.location.origin) return;
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) base.searchParams.set('preview_theme_id', preview);
      base.searchParams.set('section_id', 'evilko-random-shirt-cards');
      const pages = shuffle(Array.from({length: pageCount}, (_, index) => index + 1)).slice(0, 6);
      const pool = new Map();
      const fetchPage = async page => {
        const url = new URL(base);
        url.searchParams.set('page', String(page));
        const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal, headers: {Accept: 'text/html'}});
        if (!response.ok) throw new Error('Catalog page unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const wrapper = parsed.querySelector('[data-en-random-page]');
        if (!wrapper || Number(wrapper.dataset.enCurrentPage) !== page) throw new Error('Invalid catalog page');
        wrapper.querySelectorAll('[data-en-card]').forEach(card => {
          const href = card.querySelector('[data-en-product-link]')?.getAttribute('href');
          if (!href) return;
          const productURL = new URL(href, base);
          if (productURL.origin !== base.origin || !productURL.pathname.includes('/products/')) return;
          const key = productURL.pathname.split('/products/').pop();
          if (!pool.has(key)) pool.set(key, card);
        });
      };
      for (let offset = 0; offset < pages.length && !interacted && !controller.signal.aborted; offset += 3) {
        await Promise.allSettled(pages.slice(offset, offset + 3).map(fetchPage));
        if (pool.size >= 12) break;
      }
      if (interacted || controller.signal.aborted || !root.isConnected || !pool.size) return;
      if (pool.size < 12) grid.querySelectorAll('[data-en-card]').forEach(card => {
        const href = card.querySelector('[data-en-product-link]')?.getAttribute('href');
        if (!href) return;
        const key = new URL(href, base).pathname.split('/products/').pop();
        if (!pool.has(key)) pool.set(key, card.cloneNode(true));
      });
      const cards = shuffle(Array.from(pool.values())).slice(0, 12);
      initializeCards(cards);
      grid.replaceChildren(...cards);
      applyFilter();
    } catch (_) {
    } finally {
      clearTimeout(timeout);
      events.forEach(name => grid.removeEventListener(name, stop));
    }
  };
  const initialize = root => {
    if (root.dataset.enReady) return;
    root.dataset.enReady = 'true';
    const designDialog = root.querySelector('[data-en-design-dialog]');
    const monthlyDialog = root.querySelector('[data-en-monthly-dialog]');
    let opener = null;
    const open = (dialog, button) => {
      if (!dialog) return;
      opener = button;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      dialog.querySelector('[data-en-close]')?.focus();
    };
    const close = dialog => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      opener?.focus();
    };
    const applySwatch = swatch => {
      const card = swatch.closest('[data-en-card]');
      const image = card?.querySelector('.en-card-image > img');
      if (!image || !swatch.dataset.enImage) return false;
      image.decoding = 'async';
      image.srcset = swatch.dataset.enImageSrcset || '';
      image.src = swatch.dataset.enImage;
      image.alt = swatch.dataset.enColorLabel;
      if (swatch.dataset.enImageWidth) image.width = Number(swatch.dataset.enImageWidth);
      if (swatch.dataset.enImageHeight) image.height = Number(swatch.dataset.enImageHeight);
      card.querySelectorAll('[data-en-product-link]').forEach(link => { link.href = swatch.href; });
      card.querySelectorAll('[data-en-swatch][role="button"]').forEach(item => item.setAttribute('aria-pressed', String(item === swatch)));
      const price = card.querySelector('[data-en-price]');
      if (price) price.textContent = swatch.dataset.enVariantPrice;
      return true;
    };
    const initializeCards = cards => {
      const colorCounts = new Map();
      cards.forEach(card => {
        card.querySelectorAll('[data-en-swatch][data-en-image]').forEach(swatch => {
          if (!card.querySelector('.en-card-image > img')) return;
          swatch.setAttribute('role', 'button');
          swatch.setAttribute('aria-pressed', 'false');
        });
        const swatches = Array.from(card.querySelectorAll('[data-en-swatch][role="button"]'));
        const available = swatches.filter(swatch => swatch.dataset.enAvailable === 'true');
        const candidates = available.length ? available : swatches;
        if (!candidates.length) return;
        const colorKey = swatch => (swatch.dataset.enColor || swatch.title).trim().toLowerCase();
        const minimum = Math.min(...candidates.map(swatch => colorCounts.get(colorKey(swatch)) || 0));
        const choices = candidates.filter(swatch => (colorCounts.get(colorKey(swatch)) || 0) === minimum);
        const selected = choices[Math.floor(Math.random() * choices.length)];
        if (applySwatch(selected)) colorCounts.set(colorKey(selected), minimum + 1);
      });
    };
    let category = 'all';
    const applyFilter = () => {
      root.dataset.filtered = String(category !== 'all');
      root.querySelectorAll('[data-en-filter]').forEach(item => item.setAttribute('aria-pressed', String(item.dataset.enFilter === category)));
      let count = 0;
      root.querySelectorAll('[data-en-card]').forEach(card => {
        card.hidden = category !== 'all' && !(card.dataset.enVibes || '').split(' ').includes(category);
        if (!card.hidden) count++;
      });
      const empty = root.querySelector('[data-en-filter-empty]');
      if (empty) empty.hidden = count !== 0;
      const status = root.querySelector('[data-en-filter-status]');
      if (status) status.textContent = status.dataset.countTemplate.replace('__COUNT__', String(count));
    };
    const grid = root.querySelector('[data-en-random-grid]');
    if (grid) shuffle(Array.from(grid.querySelectorAll('[data-en-card]'))).forEach(card => grid.append(card));
    initializeCards(Array.from(root.querySelectorAll('[data-en-card]')));
    if (grid) void randomCards(root, grid, initializeCards, applyFilter);
    root.addEventListener('keydown', event => {
      const swatch = event.target.closest('[data-en-swatch][role="button"]');
      if (!swatch || event.key !== ' ' || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      event.preventDefault();
      swatch.click();
    });
    root.addEventListener('click', event => {
      const swatch = event.target.closest('[data-en-swatch]');
      if (swatch && root.contains(swatch)) {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.defaultPrevented) return;
        if (!applySwatch(swatch)) return;
        event.preventDefault();
        return;
      }
      const button = event.target.closest('button');
      if (!button || !root.contains(button)) return;
      if (button.dataset.enFilter) {
        category = button.dataset.enFilter;
        applyFilter();
      }
      if (button.dataset.enDesign) {
        const name = root.querySelector('[data-en-design-name]');
        if (name) name.textContent = button.dataset.enDesign;
        open(designDialog, button);
      }
      if (button.hasAttribute('data-en-monthly')) open(monthlyDialog, button);
      if (button.hasAttribute('data-en-close')) close(button.closest('dialog'));
      if (button.hasAttribute('data-en-newsletter-focus')) {
        close(button.closest('dialog'));
        const input = root.querySelector('.en-newsletter-input');
        input?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'center'});
        input?.focus({preventScroll:true});
      }
    });
    root.querySelectorAll('dialog').forEach(dialog => {
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close(dialog);
      });
      dialog.addEventListener('cancel', () => { opener?.focus(); });
    });
    document.addEventListener('cart:updated', event => {
      const cart = event.detail?.cart || event.detail;
      if (!Number.isFinite(cart?.item_count)) return;
      const count = root.querySelector('[data-en-cart-count]');
      if (count) count.textContent = String(cart.item_count);
      const link = root.querySelector('[data-en-cart-label]');
      if (link) link.setAttribute('aria-label', link.dataset.labelTemplate.replace('__COUNT__', String(cart.item_count)));
    });
  };
  document.querySelectorAll('[data-en-home]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-en-home]').forEach(initialize));
})();

